// Centralized Data Engine for the Benchmark Report
export const reportData = {
  global: {
    title: "Shadow AI Benchmark Report 2026",
    subtitle: "Enterprise AI Governance Data (n=400)",
    methodology: "Survey of 400 CISOs, CIOs, and IT leaders across Fortune 1000 organizations, conducted Q4 2025.",
    keyStats: [
      { value: "86%", label: "Report active Shadow AI usage" },
      { value: "75%", label: "Lack automated discovery tools" },
      { value: "57%", label: "Experienced an AI-related data leak" }
    ]
  },
  sections: [
    {
      id: "visibility-inventory",
      slug: "ai-governance-visibility-report",
      title: "Visibility & Inventory",
      description: "The baseline of governance is visibility. You cannot secure what you cannot see. Our data reveals a critical gap between assumed AI inventory and actual deployments.",
      questions: ["how-many-ai-tools-enterprise", "shadow-ai-discovery-time", "unmanaged-ai-spend-metrics"]
    },
    {
      id: "risk-incidents",
      slug: "shadow-ai-risk-incidents-report",
      title: "Risk & Incidents",
      description: "Unmanaged AI is not a theoretical risk; it is an active threat vector. We quantify the frequency, severity, and response times of enterprise AI incidents.",
      questions: ["ai-incident-frequency-2026", "board-level-ai-reporting-confidence", "average-time-to-contain-ai-breach"]
    },
    {
      id: "governance-policy",
      slug: "ai-governance-policy-report",
      title: "Governance & Policy",
      description: "Policies are only as effective as their enforcement. The benchmark highlights widespread failure in translating written acceptable use policies into technical controls.",
      questions: ["ai-governance-policy-enforcement", "ai-tool-approval-rate-enterprise", "exception-management-ai-tools"]
    },
    {
      id: "tool-sprawl",
      slug: "enterprise-ai-tool-sprawl",
      title: "Tool Sprawl & Adoption",
      description: "Grassroots adoption of AI is outpacing procurement. We analyze the fragmentation of AI tools across business units.",
      questions: ["generative-ai-adoption-rates", "departmental-ai-sprawl-analysis", "employee-bypassing-it-controls", "consolidation-priority-ai-vendors"]
    },
    {
      id: "data-security",
      slug: "ai-data-exposure-report",
      title: "Data Security & Exposure",
      description: "The primary risk of Shadow AI is the uncontrolled ingestion of sensitive corporate data into unvetted public models.",
      questions: ["shadow-ai-data-exposure-risk", "pii-in-unapproved-ai-models", "third-party-ai-vendor-risk"]
    },
    {
      id: "future-readiness",
      slug: "ai-regulation-readiness",
      title: "Future Readiness",
      description: "With sweeping regulatory changes looming (EU AI Act, etc.), enterprises are scrambling to build defensible compliance postures.",
      questions: ["future-ai-regulation-readiness"]
    }
  ],
  questions: {
    "how-many-ai-tools-enterprise": {
      title: "How Many AI Tools Are Enterprises Using? (2026 Benchmark Data)",
      hook: "Most enterprises believe they have fewer than 50 AI tools. 41% actually report 200+ upon deep inspection.",
      data: [
        { label: "Fewer than 50", value: 12 },
        { label: "50 - 99", value: 24 },
        { label: "100 - 199", value: 23 },
        { label: "200+", value: 41, highlight: true }
      ],
      insight: "The disparity between estimated and actual AI tool usage represents a profound blind spot. IT procurement typically tracks enterprise licenses (e.g., Copilot, ChatGPT Enterprise), while employees rapidly adopt ad-hoc, freemium, or departmental tools via browser extensions and personal accounts.",
      whatThisMeans: "If your official inventory lists fewer than 50 tools, it is almost certainly incomplete. Reporting a clean, low number to the board indicates a lack of visibility, not a secure environment.",
      riskLevel: "CRITICAL",
      riskText: "High probability of fragmented data siloes and unvetted third-party terms of service.",
      related: ["shadow-ai-discovery-time", "ai-tool-approval-rate-enterprise"]
    },
    "shadow-ai-discovery-time": {
      title: "How Quickly Are Unmanaged AI Tools Discovered?",
      hook: "Speed to detection is a primary indicator of governance maturity. Over 60% of enterprises take weeks or months to detect a new Shadow AI tool.",
      data: [
        { label: "Real-time / < 24 hours", value: 14 },
        { label: "Days", value: 22 },
        { label: "Weeks", value: 45, highlight: true },
        { label: "Months / Only during audits", value: 19 }
      ],
      insight: "Relying on manual surveys, CASB logs tuned for legacy SaaS, or annual audits is insufficient for the speed of AI adoption. New AI tools are deployed daily, often bypassing traditional network perimeters via mobile or BYOD.",
      whatThisMeans: "A lag of weeks in discovery means sensitive data could be ingested and used to train public models long before security teams are even aware the tool exists.",
      riskLevel: "HIGH",
      riskText: "Delayed detection significantly widens the window for data exfiltration.",
      related: ["how-many-ai-tools-enterprise", "average-time-to-contain-ai-breach"]
    },
    "unmanaged-ai-spend-metrics": {
      title: "What is the Financial Impact of Unmanaged AI?",
      hook: "Shadow AI isn't just a security risk; it's a financial black hole. 52% of organizations report over $100k in duplicate or unmanaged AI spend.",
      data: [
        { label: "Under $10k", value: 18 },
        { label: "$10k - $50k", value: 30 },
        { label: "$50k - $100k", value: 25 },
        { label: "Over $100k", value: 27, highlight: true }
      ],
      insight: "Business units are expensing AI subscriptions on corporate cards to bypass slow IT procurement cycles. This leads to duplicate licenses, higher per-seat costs, and failure to leverage enterprise discounting.",
      whatThisMeans: "Consolidating Shadow AI visibility immediately pays for itself by identifying redundant licenses and migrating users to approved, enterprise-tier contracts.",
      riskLevel: "MEDIUM",
      riskText: "Financial inefficiency and breach of procurement protocols.",
      related: ["how-many-ai-tools-enterprise", "departmental-ai-sprawl-analysis"]
    },
    "ai-incident-frequency-2026": {
      title: "How Often Do AI-Related Security Incidents Occur?",
      hook: "AI incidents are no longer rare edge cases. 68% of enterprises experienced at least one AI-related security incident in the past 12 months.",
      data: [
        { label: "Zero incidents", value: 32 },
        { label: "1 - 2 incidents", value: 41, highlight: true },
        { label: "3 - 5 incidents", value: 18 },
        { label: "6+ incidents", value: 9 }
      ],
      insight: "The normalization of AI incidents suggests that current preventative controls are failing. These incidents range from accidental source code leaks to prompt injection attacks against internal LLMs.",
      whatThisMeans: "Incident response playbooks must be updated immediately to include AI-specific threat vectors. Assuming zero incidents often means zero detection capability.",
      riskLevel: "CRITICAL",
      riskText: "Active threat environment with high likelihood of recurrence.",
      related: ["average-time-to-contain-ai-breach", "board-level-ai-reporting-confidence"]
    },
    "board-level-ai-reporting-confidence": {
      title: "CISO Confidence in Board-Level AI Reporting",
      hook: "Only 15% of CISOs feel 'Highly Confident' in the AI risk metrics they present to the Board of Directors.",
      data: [
        { label: "Highly Confident", value: 15 },
        { label: "Somewhat Confident", value: 42, highlight: true },
        { label: "Low Confidence", value: 31 },
        { label: "Do not report on AI", value: 12 }
      ],
      insight: "Boards are demanding quantifiable metrics on AI risk, but security leaders lack the automated tooling to provide accurate data. Manual estimates and qualitative assessments are no longer sufficient.",
      whatThisMeans: "If your CEO or Board asked today for a definitive list of AI tools in use and their associated risks, a manual response will undermine leadership confidence.",
      riskLevel: "HIGH",
      riskText: "Reputational risk for security leadership during board reviews.",
      related: ["ai-governance-policy-enforcement", "future-ai-regulation-readiness"]
    },
    "average-time-to-contain-ai-breach": {
      title: "Average Time to Contain an AI Data Exposure",
      hook: "When AI data exposures occur, they are difficult to contain. 54% take more than 48 hours to fully remediate.",
      data: [
        { label: "< 12 hours", value: 12 },
        { label: "12 - 48 hours", value: 34 },
        { label: "2 - 7 days", value: 38, highlight: true },
        { label: "Over 1 week", value: 16 }
      ],
      insight: "Unlike traditional SaaS breaches where access can be revoked, data ingested into an external LLM cannot easily be 'deleted'. Containment often requires complex legal and vendor negotiations.",
      whatThisMeans: "Prevention and real-time blocking are vastly superior to post-incident response in the AI context. Once data is in a public model, the damage is largely done.",
      riskLevel: "CRITICAL",
      riskText: "Irreversible data exposure and potential regulatory fines.",
      related: ["shadow-ai-data-exposure-risk", "ai-incident-frequency-2026"]
    },
    "ai-governance-policy-enforcement": {
      title: "Are AI Acceptable Use Policies Actually Enforced?",
      hook: "88% of enterprises have a written AI policy. Only 22% have technical controls to enforce it.",
      data: [
        { label: "Written policy only", value: 66, highlight: true },
        { label: "Technical enforcement (Partial)", value: 20 },
        { label: "Technical enforcement (Full)", value: 12 },
        { label: "No policy", value: 2 }
      ],
      insight: "A PDF on the intranet does not constitute AI governance. Employees routinely ignore acceptable use policies in favor of productivity gains when technical guardrails are absent.",
      whatThisMeans: "Your written policy is a liability if you cannot prove enforcement. Regulators and auditors look for technical controls, not just documentation.",
      riskLevel: "HIGH",
      riskText: "False sense of security; high compliance failure risk.",
      related: ["employee-bypassing-it-controls", "ai-tool-approval-rate-enterprise"]
    },
    "ai-tool-approval-rate-enterprise": {
      title: "What is the Approval Rate for New Enterprise AI Tools?",
      hook: "IT bottlenecks are driving Shadow AI. 73% of AI tool requests take over a month to process, leading users to bypass the system.",
      data: [
        { label: "< 1 week", value: 8 },
        { label: "1 - 4 weeks", value: 19 },
        { label: "1 - 3 months", value: 45, highlight: true },
        { label: "3+ months / Denied", value: 28 }
      ],
      insight: "Rigid, legacy procurement processes cannot keep pace with the generative AI market. When the business needs an AI capability today, a three-month security review guarantees the creation of Shadow AI.",
      whatThisMeans: "Security teams must transition from 'gatekeepers' to 'enablers' by implementing automated, rapid risk-scoring for new AI applications.",
      riskLevel: "MEDIUM",
      riskText: "Operational friction driving insecure circumvention.",
      related: ["shadow-ai-discovery-time", "exception-management-ai-tools"]
    },
    "exception-management-ai-tools": {
      title: "How Are AI Policy Exceptions Managed?",
      hook: "Exceptions are becoming the rule. 47% of enterprises manage AI tool exceptions via ad-hoc emails or Slack messages.",
      data: [
        { label: "Dedicated workflow tool", value: 24 },
        { label: "ITSM Tickets (e.g., Jira/ServiceNow)", value: 29 },
        { label: "Email / Chat (Ad-hoc)", value: 47, highlight: true }
      ],
      insight: "Informal exception management destroys the audit trail. When an unvetted tool causes an incident, tracing the approval back through unstructured channels is nearly impossible.",
      whatThisMeans: "You must establish a centralized, auditable system of record for all AI tool approvals, denials, and conditional exceptions.",
      riskLevel: "HIGH",
      riskText: "Audit trail failure and lack of accountability.",
      related: ["ai-governance-policy-enforcement", "ai-tool-approval-rate-enterprise"]
    },
    "generative-ai-adoption-rates": {
      title: "Generative AI Adoption Rates Across Departments",
      hook: "Marketing and Engineering lead GenAI adoption, but HR and Legal are adopting faster than IT realizes, introducing severe privacy risks.",
      data: [
        { label: "Engineering / R&D", value: 35 },
        { label: "Marketing / Sales", value: 32 },
        { label: "HR / Legal / Finance", value: 21, highlight: true },
        { label: "Operations", value: 12 }
      ],
      insight: "While code generation and copywriting are well-known use cases, the covert use of GenAI to summarize resumes, contracts, or financial statements poses a massive, unquantified regulatory risk.",
      whatThisMeans: "Visibility efforts must extend beyond technical teams. The most sensitive data often resides in departments with the lowest security awareness.",
      riskLevel: "CRITICAL",
      riskText: "High risk of PII/MNPI exposure in non-technical departments.",
      related: ["departmental-ai-sprawl-analysis", "pii-in-unapproved-ai-models"]
    },
    "departmental-ai-sprawl-analysis": {
      title: "Departmental AI Sprawl Analysis",
      hook: "The average enterprise has 4 different generative AI platforms performing the exact same function across different departments.",
      data: [
        { label: "Highly Consolidated (1-2 tools)", value: 15 },
        { label: "Moderate Sprawl (3-5 tools)", value: 31 },
        { label: "Severe Sprawl (6+ redundant tools)", value: 54, highlight: true }
      ],
      insight: "Siloed purchasing leads to overlapping capabilities, inconsistent security postures, and fractured data governance. Marketing uses Jasper, Sales uses Copy.ai, and IT uses ChatGPT Enterprise.",
      whatThisMeans: "Identifying redundant tools is the fastest way to build ROI for an AI governance program while simultaneously reducing the attack surface.",
      riskLevel: "MEDIUM",
      riskText: "Inefficient spend and inconsistent security baselines.",
      related: ["generative-ai-adoption-rates", "unmanaged-ai-spend-metrics"]
    },
    "employee-bypassing-it-controls": {
      title: "Why Do Employees Bypass IT AI Controls?",
      hook: "Malice is rarely the motive. 62% of employees bypass AI controls simply because 'approved tools lack required features'.",
      data: [
        { label: "Approved tools lack features", value: 62, highlight: true },
        { label: "Approval process takes too long", value: 24 },
        { label: "Unaware of policy", value: 10 },
        { label: "Intentional evasion", value: 4 }
      ],
      insight: "Users are optimizing for productivity. When corporate-approved AI models are heavily restricted or outdated compared to consumer versions, employees will find workarounds (e.g., using personal devices).",
      whatThisMeans: "Draconian blocking without providing capable, secure alternatives will fail. Security must offer a 'paved road' for AI adoption.",
      riskLevel: "HIGH",
      riskText: "Shadow IT proliferation driven by business friction.",
      related: ["ai-governance-policy-enforcement", "ai-tool-approval-rate-enterprise"]
    },
    "consolidation-priority-ai-vendors": {
      title: "Is Consolidating AI Vendors a Priority?",
      hook: "78% of CIOs list 'AI Vendor Consolidation' as a Top 3 priority for 2026, yet lack the data to execute it.",
      data: [
        { label: "Top Priority (Active Project)", value: 34 },
        { label: "High Priority (Planning)", value: 44, highlight: true },
        { label: "Low Priority", value: 18 },
        { label: "Not a Priority", value: 4 }
      ],
      insight: "Consolidation requires comprehensive visibility. You cannot sunset redundant AI tools if you don't know who is using them, what data they process, and what business processes rely on them.",
      whatThisMeans: "Before initiating a vendor consolidation initiative, a comprehensive, automated Shadow AI audit is mandatory to prevent business disruption.",
      riskLevel: "MEDIUM",
      riskText: "Strategic initiatives stalled due to lack of visibility data.",
      related: ["departmental-ai-sprawl-analysis", "unmanaged-ai-spend-metrics"]
    },
    "shadow-ai-data-exposure-risk": {
      title: "What is the Primary Data Exposure Risk in Shadow AI?",
      hook: "Source code is no longer the top concern. 48% of identified AI exposures involve Customer PII or Financial Data.",
      data: [
        { label: "Customer PII", value: 48, highlight: true },
        { label: "Source Code / IP", value: 29 },
        { label: "Financial Data (MNPI)", value: 15 },
        { label: "Strategic Plans", value: 8 }
      ],
      insight: "Early AI fears focused on developers leaking code. Today, the broader business use of AI for transcription, translation, and data analysis means highly regulated data is routinely fed into unmanaged models.",
      whatThisMeans: "Data Loss Prevention (DLP) strategies must be updated to inspect prompts and interactions with AI endpoints, not just file uploads.",
      riskLevel: "CRITICAL",
      riskText: "Direct violation of GDPR, CCPA, and other privacy frameworks.",
      related: ["pii-in-unapproved-ai-models", "average-time-to-contain-ai-breach"]
    },
    "pii-in-unapproved-ai-models": {
      title: "Frequency of PII Ingestion in Unapproved AI Models",
      hook: "Alarmingly, 39% of enterprises caught employees pasting raw customer data into consumer-grade AI chatbots.",
      data: [
        { label: "Weekly occurrences", value: 39, highlight: true },
        { label: "Monthly occurrences", value: 27 },
        { label: "Rarely", value: 22 },
        { label: "Never (Confirmed)", value: 12 }
      ],
      insight: "Consumer-grade AI models explicitly state in their terms of service that user inputs may be used to train future models. Pasting PII here is a direct, unmitigated data leak.",
      whatThisMeans: "If you lack prompt-level visibility and blocking capabilities, you must assume PII is actively leaving your perimeter via AI chat interfaces.",
      riskLevel: "CRITICAL",
      riskText: "Severe compliance breaches and loss of data sovereignty.",
      related: ["shadow-ai-data-exposure-risk", "employee-bypassing-it-controls"]
    },
    "third-party-ai-vendor-risk": {
      title: "Assessing Third-Party AI Vendor Risk",
      hook: "Only 18% of enterprises rigorously assess the AI capabilities embedded within their existing SaaS supply chain.",
      data: [
        { label: "Rigorous AI-specific assessment", value: 18 },
        { label: "Standard vendor review (No AI specifics)", value: 54, highlight: true },
        { label: "Rely on vendor attestations", value: 22 },
        { label: "No assessment", value: 6 }
      ],
      insight: "Shadow AI isn't just new tools; it's existing vendors silently rolling out 'AI features' (e.g., Notion AI, Zoom AI Companion) that change data processing flows without explicit IT approval.",
      whatThisMeans: "Your Third-Party Risk Management (TPRM) program must automatically detect and evaluate sub-processors and AI feature toggles within established SaaS applications.",
      riskLevel: "HIGH",
      riskText: "Silent expansion of attack surface via trusted vendors.",
      related: ["how-many-ai-tools-enterprise", "shadow-ai-data-exposure-risk"]
    },
    "future-ai-regulation-readiness": {
      title: "Are Enterprises Ready for AI Regulation? (EU AI Act)",
      hook: "Despite looming deadlines, 71% of organizations admit they are not prepared to comply with frameworks like the EU AI Act.",
      data: [
        { label: "Fully Prepared", value: 9 },
        { label: "Somewhat Prepared", value: 20 },
        { label: "Unprepared (Planning Phase)", value: 48, highlight: true },
        { label: "Unprepared (No Action Taken)", value: 23 }
      ],
      insight: "Upcoming regulations require comprehensive inventories of automated decision systems, risk categorizations, and transparency reports. You cannot comply if you cannot see your AI footprint.",
      whatThisMeans: "Waiting for regulations to take effect before building an AI inventory ensures a panicked, highly expensive compliance scramble. Visibility is the prerequisite for compliance.",
      riskLevel: "HIGH",
      riskText: "Impending regulatory fines and operational injunctions.",
      related: ["board-level-ai-reporting-confidence", "ai-governance-policy-enforcement"]
    }
  }
};